import * as React from 'react'
import clsx from 'clsx'
import styled, { css } from 'styled-components'
import { rem } from 'polished'

import { REBRAND_PALETTE_CLASS } from 'lib/theme/paletteVersion'

import LineBedIcon from 'LuxKit/Icons/line/LineBedIcon'
import LineBusIcon from 'LuxKit/Icons/line/LineBusIcon'
import LineCameraPlusIcon from 'LuxKit/Icons/line/LineCameraPlusIcon'
import LineCoffeeIcon from 'LuxKit/Icons/line/LineCoffeeIcon'
import LineDrinksTrayIcon from 'LuxKit/Icons/line/LineDrinksTrayIcon'
import LineFootballBallIcon from 'LuxKit/Icons/line/LineFootballBallIcon'
import LineMapMarkerIcon from 'LuxKit/Icons/line/LineMapMarkerIcon'
import LinePlaneFlyIcon from 'LuxKit/Icons/line/LinePlaneFlyIcon'
import LineRestaurantIcon from 'LuxKit/Icons/line/LineRestaurantIcon'
import LineShipIcon from 'LuxKit/Icons/line/LineShipIcon'
import LineShoppingBagIcon from 'LuxKit/Icons/line/LineShoppingBagIcon'
import LineTicketIcon from 'LuxKit/Icons/line/LineTicketIcon'
import LineTreesIcon from 'LuxKit/Icons/line/LineTreesIcon'
import LineUniversityIcon from 'LuxKit/Icons/line/LineUniversityIcon'
import {
  TRIP_LIST_CATEGORY_COLOUR_GROUPS,
  TRIP_LIST_CATEGORY_COLOURS,
  type TripListCategoryColourGroup,
} from './tripListCategoryColours'
import { getTripListPlaceCategory, type TripListPlaceCategory } from './tripListPlaceCategories'
import type { TripListRow, TripListRowType } from './tripListTypes'

export type TripListIconCategory =
  | 'accommodation'
  | 'experience'
  | TripListPlaceCategory
  | 'transport-filter'

interface TripListIconCategoryInput {
  type: TripListRowType
  primaryType?: string
  placeTypes?: ReadonlyArray<string>
  placeCategory?: string
  hasRow: boolean
}

/*
 * Rebrand colours the chips by Trip Kit category rather than by row type. Row
 * type is too coarse: `activity` alone spans parks, museums and shopping, which
 * the Trip Kit spec splits across three different colour groups.
 */
const rebrandCategoryColours = TRIP_LIST_CATEGORY_COLOUR_GROUPS.map(group => css`
  body.${REBRAND_PALETTE_CLASS} &.category-${group} {
    background: ${TRIP_LIST_CATEGORY_COLOURS[group].background};
    color: ${TRIP_LIST_CATEGORY_COLOURS[group].foreground};
  }
`)

const TypeIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${rem(28)};
  height: ${rem(28)};
  /* Trip Kit 3.0 squares these off -- a rounded square, not a pill. */
  border-radius: ${props => props.theme.borderRadius.M};

  /* Figma's desktop table row packs the chip into 24px on a 4px radius so the
     whole row still fits its 52px band. */
  &.size-small {
    width: ${rem(24)};
    height: ${rem(24)};
    border-radius: ${props => props.theme.borderRadius.S};
  }
  background: ${props => props.theme.palette.neutral.surface.lightBackground};
  color: ${props => props.theme.palette.neutral.surface.normalContrast};
  flex-shrink: 0;

  &.type-flight {
    background: ${props => props.theme.palette.product.flights.seats.upfront.lightBackground};
    color: ${props => props.theme.palette.product.flights.seats.upfront.normalForeground};
  }

  &.type-accommodation,
  &.type-experience {
    background: ${props => props.theme.palette.highlight.secondary.lightBackground};
    color: ${props => props.theme.palette.highlight.secondary.normalForeground};
  }

  &.type-dining {
    background: ${props => props.theme.palette.messaging.warning.lightBackground};
    color: ${props => props.theme.palette.messaging.warning.normalForeground};
  }

  &.type-activity {
    background: ${props => props.theme.palette.messaging.success.lightBackground};
    color: ${props => props.theme.palette.messaging.success.normalForeground};
  }

  &.type-transport,
  &.type-cruise {
    background: ${props => props.theme.palette.highlight.primary.lightBackground};
    color: ${props => props.theme.palette.highlight.primary.normalForeground};
  }

  ${rebrandCategoryColours}
`

export function getTripListIconCategory(input: TripListIconCategoryInput): TripListIconCategory {
  switch (input.type) {
    case 'accommodation':
      return 'accommodation'
    case 'experience':
      return 'experience'
    case 'dining': {
      const category = getTripListPlaceCategory({
        primaryType: input.primaryType,
        types: input.placeTypes,
        primaryTypeDisplayName: input.placeCategory,
      })
      return category === 'attraction' ? 'restaurant' : category
    }
    case 'flight':
      return 'flight'
    case 'transport':
      return input.hasRow ? 'transport' : 'transport-filter'
    case 'cruise':
      return 'cruise'
    case 'activity':
      return getTripListPlaceCategory({
        primaryType: input.primaryType,
        types: input.placeTypes,
        primaryTypeDisplayName: input.placeCategory,
      })
    default:
      return 'attraction'
  }
}

export function getTripListCategoryColourGroup(
  category: TripListIconCategory,
): TripListCategoryColourGroup {
  switch (category) {
    case 'accommodation':
    case 'lodging':
      return 'hotel'
    case 'restaurant':
    case 'cafe':
    case 'bar':
      return 'food-and-drink'
    case 'park':
    case 'sport':
      return 'active-and-outdoor'
    case 'shopping':
      return 'shopping'
    case 'flight':
    case 'transport':
    case 'transport-filter':
    case 'cruise':
      return 'transport'
    case 'experience':
    case 'museum':
    case 'attraction':
      return 'entertainment-and-leisure'
  }
}

function getTripListIconCategoryInput(type: TripListRowType, row: TripListRow | undefined): TripListIconCategoryInput {
  const placeDetail = row?.kind === 'item' ? row.item.itemPlaceDetail : undefined

  return {
    type,
    primaryType: placeDetail?.primaryType,
    placeTypes: placeDetail?.types,
    placeCategory: placeDetail?.primaryTypeDisplayName,
    hasRow: !!row,
  }
}

function getIcon(category: TripListIconCategory, size: 'default' | 'small' = 'default'): React.ReactNode {
  switch (category) {
    case 'accommodation':
      return <LineBedIcon size={size === 'small' ? 'XXS' : 'S'} />
    case 'experience':
      return <LineTicketIcon size={size === 'small' ? 'XXS' : 'S'} />
    case 'restaurant':
      return <LineRestaurantIcon size={size === 'small' ? 'XXS' : 'S'} />
    case 'cafe':
      return <LineCoffeeIcon size={size === 'small' ? 'XXS' : 'S'} />
    case 'park':
      return <LineTreesIcon size={size === 'small' ? 'XXS' : 'S'} />
    case 'shopping':
      return <LineShoppingBagIcon size={size === 'small' ? 'XXS' : 'S'} />
    case 'museum':
      return <LineUniversityIcon size={size === 'small' ? 'XXS' : 'S'} />
    case 'bar':
      return <LineDrinksTrayIcon size={size === 'small' ? 'XXS' : 'S'} />
    case 'sport':
      return <LineFootballBallIcon size={size === 'small' ? 'XXS' : 'S'} />
    case 'flight':
      return <LinePlaneFlyIcon size={size === 'small' ? 'XXS' : 'S'} />
    case 'transport':
      return <LineBusIcon size={size === 'small' ? 'XXS' : 'S'} />
    case 'transport-filter':
      return <LineMapMarkerIcon size={size === 'small' ? 'XXS' : 'S'} />
    case 'lodging':
      return <LineBedIcon size={size === 'small' ? 'XXS' : 'S'} />
    case 'cruise':
      return <LineShipIcon size={size === 'small' ? 'XXS' : 'S'} />
    case 'attraction':
      return <LineCameraPlusIcon size={size === 'small' ? 'XXS' : 'S'} />
    default:
      return null
  }
}

interface Props {
  type: TripListRowType
  row?: TripListRow
  className?: string
  /** `small` is the 24px desktop table chip; `default` is the 28px card chip. */
  size?: 'default' | 'small'
}

function TripListTypeIcon({ type, row, className, size = 'default' }: Props) {
  const iconCategory = getTripListIconCategory(getTripListIconCategoryInput(type, row))

  return (
    <TypeIcon
      data-testid="trip-list-type-icon"
      className={clsx(
        `type-${type}`,
        `category-${getTripListCategoryColourGroup(iconCategory)}`,
        size === 'small' && 'size-small',
        className,
      )}
    >
      {getIcon(iconCategory, size)}
    </TypeIcon>
  )
}

export default TripListTypeIcon
